import { getCurrentSession } from '../auth.ts';
import { normalizeTimestampToMs } from '../time/normalizeTimestampToMs';
import { normalizeTradeAssetClassification } from '../trades/canonicalAssetClass';
import { normalizeVisibleTradeIds } from '../trades/visibleTradeIds';
import { applyTenantRequestHeaders } from '../tenantRequestContext';
import { getPrivateTradingScopeGeneration, getVerifiedPrivateTradingScope, isPrivateTradingScopeCurrent, subscribePrivateTradingScope } from '../storage/privateTradingStorage';

export type PersistedCsvTradeExecution = Record<string, any>;
export type PersistedCsvTradeRecord = Record<string, any>;

type PersistedCsvTradesResponse = {
  ok?: boolean;
  source?: string;
  count?: number;
  limit?: number;
  offset?: number;
  trades?: unknown;
};

const DEFAULT_LIMIT = 500;
const MAX_LIMIT = 500;
const DEFAULT_ACCOUNT_TAGS = new Set(['', 'default', 'default-account', 'default account']);

let persistedTradesCache: PersistedCsvTradeRecord[] | null = null;
let persistedTradesCacheScope = '';
let persistedTradesCacheReady = false;
let persistedTradesCachePromise: Promise<PersistedCsvTradeRecord[]> | null = null;
let persistedTradesRequestGeneration = 0;

subscribePrivateTradingScope(() => resetPersistedCsvTradesCache());

function getBase(): string {
  const envBase = String((import.meta as any).env?.VITE_API_BASE || '').trim();
  return envBase.replace(/\/+$/, '');
}

function buildUrl(path: string): string {
  const base = getBase();
  return base ? `${base}${path}` : path;
}

function toTrimmedString(value: unknown): string {
  return String(value ?? '').trim();
}

function toFiniteNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function toPositiveInt(value: unknown, fallback: number): number {
  const numeric = Number.parseInt(String(value ?? '').trim(), 10);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  return Math.min(numeric, MAX_LIMIT);
}

function normalizeSourceType(value: unknown): string {
  const raw = toTrimmedString(value).toLowerCase();
  return raw || 'csv';
}

function normalizeSourceBroker(value: unknown): string {
  const raw = toTrimmedString(value).toLowerCase();
  if (!raw || raw === 'auto') return 'csv_generic';
  return raw.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'csv_generic';
}

function normalizeAccountTag(value: unknown): string {
  return toTrimmedString(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function isMeaningfulAccountTag(value: unknown): boolean {
  const normalized = normalizeAccountTag(value);
  return Boolean(normalized) && !DEFAULT_ACCOUNT_TAGS.has(normalized);
}

function normalizeCanonicalAssetClass(value: unknown): 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO' | 'UNKNOWN' {
  const raw = toTrimmedString(value).toUpperCase();
  if (!raw) return 'UNKNOWN';
  if (raw.includes('FUT')) return 'FUTURES';
  if (raw === 'FX' || raw.includes('FOREX') || raw.includes('CASH') || raw.includes('CURRENCY')) return 'FOREX';
  if (raw.includes('CRYPTO')) return 'CRYPTO';
  if (raw.includes('STK') || raw.includes('STOCK') || raw.includes('EQUITY') || raw === 'INDEX_CFD' || raw === 'CASH_INDEX') return 'STOCKS';
  if (raw === 'UNKNOWN') return 'UNKNOWN';
  return 'UNKNOWN';
}

function toLegacyAssetClass(canonicalAssetClass: 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO' | 'UNKNOWN'): string {
  if (canonicalAssetClass === 'FOREX') return 'CASH';
  return canonicalAssetClass;
}

function normalizeRawInstrumentType(value: unknown): 'STK' | 'FUT' | 'CASH' | 'CRYPTO' | 'UNKNOWN' {
  const raw = toTrimmedString(value).toUpperCase();
  if (!raw) return 'UNKNOWN';
  if (raw === 'STK' || raw === 'STOCK' || raw === 'STOCKS' || raw === 'INDEX_CFD' || raw === 'CASH_INDEX') return 'STK';
  if (raw === 'FUT' || raw === 'FUTURE' || raw === 'FUTURES') return 'FUT';
  if (raw === 'CASH' || raw === 'FX' || raw === 'FOREX') return 'CASH';
  if (raw === 'CRYPTO') return 'CRYPTO';
  return 'UNKNOWN';
}

function normalizeExecutionSide(value: unknown): 'buy' | 'sell' {
  const raw = toTrimmedString(value).toLowerCase();
  if (raw === 'sell' || raw === 'short' || raw === 's') return 'sell';
  return 'buy';
}

function hashStringToPositiveInt(input: string): number {
  let hash = 5381;
  for (let index = 0; index < input.length; index += 1) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(index);
  }
  return (hash >>> 0) || 1;
}

function buildExecutionDedupeKey(exec: any): string {
  const symbol = String(exec?.symbol ?? '').trim().toUpperCase();
  const side = String(exec?.side ?? '').trim().toLowerCase();
  const qty = Number(exec?.qty ?? exec?.quantity ?? 0);
  const price = Number(exec?.price ?? 0);
  const assetClass = String(exec?.assetClass ?? '').trim().toUpperCase();
  const orderId = String(exec?.orderId ?? exec?.externalOrderId ?? '').trim();
  const ts = normalizeTimestampToMs(exec?.timestamp ?? exec?.time ?? exec?.executedAt) ?? 0;

  return [symbol, side, Number.isFinite(qty) ? qty : 0, Number.isFinite(price) ? price : 0, assetClass, orderId, ts].join('|');
}

function getExecutions(tradeLike: any): any[] {
  if (Array.isArray(tradeLike?.executionsList)) return tradeLike.executionsList;
  if (Array.isArray(tradeLike?.executions)) return tradeLike.executions;
  return [];
}

function buildOpeningExecutionKey(tradeLike: any): string | null {
  const executions = getExecutions(tradeLike);
  if (!executions.length) return null;
  const first = executions.find((entry) => entry && typeof entry === 'object');
  if (!first) return null;
  return buildExecutionDedupeKey(first);
}

function deriveSourceAccountRef(tradeLike: any): string | null {
  const explicit = toTrimmedString(tradeLike?.sourceAccountRef ?? tradeLike?.source_account_ref);
  if (explicit) return explicit;

  const accountCandidate = tradeLike?.account ?? tradeLike?.accountTag ?? tradeLike?.providerAccountId ?? tradeLike?.accountId;
  if (isMeaningfulAccountTag(accountCandidate)) {
    return `ibkr_csv:label:${normalizeAccountTag(accountCandidate)}`;
  }

  const providerAccountId = toTrimmedString(tradeLike?.providerAccountId ?? tradeLike?.accountId);
  if (providerAccountId) {
    return `ibkr_csv:${providerAccountId.replace(/\s+/g, '').toUpperCase()}`;
  }

  return null;
}

function deriveTradeIdentityKey(tradeLike: any): string | null {
  const sourceType = normalizeSourceType(tradeLike?.sourceType ?? tradeLike?.source ?? tradeLike?.source_type);
  if (sourceType !== 'csv' && sourceType !== 'broker_sync') return null;

  const sourceBroker = normalizeSourceBroker(tradeLike?.sourceBroker ?? tradeLike?.broker ?? tradeLike?.brokerId ?? tradeLike?.source_broker);
  const sourceAccountRef = deriveSourceAccountRef(tradeLike);
  if (!sourceAccountRef) return null;

  const openingExecutionKey = buildOpeningExecutionKey(tradeLike);
  if (!openingExecutionKey) return null;

  const symbol = toTrimmedString(tradeLike?.symbol).toUpperCase();
  const rawInstrumentType = normalizeRawInstrumentType(tradeLike?.rawInstrumentType ?? tradeLike?.secType ?? tradeLike?.instrumentType ?? tradeLike?.assetClass ?? tradeLike?.canonicalAssetClass);

  return [sourceType, sourceBroker, sourceAccountRef, symbol, rawInstrumentType, openingExecutionKey].join('|');
}

function derivePersistedManualIdentityKey(tradeLike: any): string | null {
  const sourceType = normalizeSourceType(tradeLike?.sourceType ?? tradeLike?.source ?? tradeLike?.source_type);
  if (sourceType !== 'manual') return null;
  const persistedId = toTrimmedString(tradeLike?.persistedTradeId ?? tradeLike?.persisted_trade_id);
  if (persistedId) return `manual:id:${persistedId}`;
  const stableKey = toTrimmedString(tradeLike?.stableDedupeKey ?? tradeLike?.stable_dedupe_key);
  return stableKey.startsWith('manual:') ? `manual:key:${stableKey}` : null;
}

function normalizePersistedCsvTradeExecution(row: any): PersistedCsvTradeExecution {
  const timestampMs = normalizeTimestampToMs(row?.execution_timestamp ?? row?.timestamp ?? row?.time ?? row?.executedAt);
  const canonicalAssetClass = normalizeCanonicalAssetClass(row?.canonical_asset_class ?? row?.canonicalAssetClass ?? row?.assetClass);
  const assetClass = toLegacyAssetClass(canonicalAssetClass);
  const quantity = toFiniteNumber(row?.quantity ?? row?.qty) ?? 0;
  const price = toFiniteNumber(row?.price) ?? 0;
  const fee = toFiniteNumber(row?.fee) ?? 0;
  const orderId = toTrimmedString(row?.external_order_id ?? row?.externalOrderId ?? row?.orderId) || undefined;
  const externalExecutionId = toTrimmedString(row?.external_execution_id ?? row?.externalExecutionId) || undefined;
  const stableExecutionDedupeKey = toTrimmedString(row?.stable_execution_dedupe_key ?? row?.stableExecutionDedupeKey) || undefined;

  return normalizeTradeAssetClassification({
    id: stableExecutionDedupeKey || toTrimmedString(row?.id) || undefined,
    persistedId: toTrimmedString(row?.id) || undefined,
    tradeId: toTrimmedString(row?.trade_id) || undefined,
    sourceType: normalizeSourceType(row?.source_type ?? row?.sourceType),
    sourceBroker: normalizeSourceBroker(row?.source_broker ?? row?.sourceBroker),
    sourceAccountRef: toTrimmedString(row?.source_account_ref ?? row?.sourceAccountRef) || undefined,
    importBatchId: toTrimmedString(row?.import_batch_id ?? row?.importBatchId) || undefined,
    stableExecutionDedupeKey,
    executionIndex: Number.isFinite(Number(row?.execution_index)) ? Number(row?.execution_index) : 0,
    symbol: toTrimmedString(row?.symbol).toUpperCase(),
    side: normalizeExecutionSide(row?.side),
    qty: quantity,
    quantity,
    price,
    fee,
    currency: toTrimmedString(row?.currency) || undefined,
    timestamp: timestampMs ?? undefined,
    executedAt: timestampMs ?? undefined,
    openTimestampMs: timestampMs ?? undefined,
    time: timestampMs ?? undefined,
    orderId,
    externalOrderId: orderId,
    externalExecutionId,
    assetClass,
    canonicalAssetClass,
    rawInstrumentType: normalizeRawInstrumentType(row?.raw_instrument_type ?? row?.rawInstrumentType),
  });
}

function normalizePersistedCsvTradeRecord(row: any): PersistedCsvTradeRecord {
  const executions: PersistedCsvTradeExecution[] = Array.isArray(row?.executions)
    ? row.executions.map((execution: PersistedCsvTradeExecution) => normalizePersistedCsvTradeExecution(execution)).filter(Boolean)
    : [];

  const openTimestampMs = normalizeTimestampToMs(
    row?.opened_at_utc
      ?? row?.openedAtUtc
      ?? row?.opened_at
      ?? row?.openTimestampMs
      ?? row?.timestamp
      ?? row?.time
      ?? row?.executedAt
      ?? executions[0]?.timestamp
  );
  const closeTimestampMs = normalizeTimestampToMs(
    row?.closed_at_utc
      ?? row?.closedAtUtc
      ?? row?.closed_at
      ?? row?.closeTimestampMs
      ?? row?.closeTime
      ?? executions[executions.length - 1]?.timestamp
  );
  const canonicalAssetClass = normalizeCanonicalAssetClass(row?.canonical_asset_class ?? row?.canonicalAssetClass ?? row?.assetClass);
  const assetClass = toLegacyAssetClass(canonicalAssetClass);
  const sourceType = normalizeSourceType(row?.source_type ?? row?.sourceType);
  const sourceBroker = normalizeSourceBroker(row?.source_broker ?? row?.sourceBroker);
  const sourceAccountRef = toTrimmedString(row?.source_account_ref ?? row?.sourceAccountRef) || null;
  const stableDedupeKey = toTrimmedString(row?.stable_dedupe_key ?? row?.stableDedupeKey) || null;
  const rawInstrumentType = normalizeRawInstrumentType(row?.raw_instrument_type ?? row?.rawInstrumentType);
  const symbol = toTrimmedString(row?.symbol).toUpperCase();
  const direction = String(row?.direction ?? '').trim().toUpperCase();
  const side = direction === 'SHORT' ? 'Short' : 'Long';
  const quantity = toFiniteNumber(row?.quantity ?? row?.volume) ?? executions.reduce((sum: number, execution: PersistedCsvTradeExecution) => sum + (Number(execution.qty ?? execution.quantity ?? 0) || 0), 0);
  const executedQuantity = Number.isFinite(quantity) ? quantity : 0;
  const executionCount = Number.isFinite(Number(row?.execution_count)) ? Number(row?.execution_count) : executions.length;
  const entryPrice = toFiniteNumber(row?.entry_price ?? row?.entryPrice);
  const exitPrice = toFiniteNumber(row?.exit_price ?? row?.exitPrice);
  const netPnl = toFiniteNumber(row?.net_pnl ?? row?.pnl);
  const grossPnl = toFiniteNumber(row?.gross_pnl ?? row?.grossPnl);
  const fees = toFiniteNumber(row?.fees_commissions ?? row?.fees ?? row?.commission) ?? 0;
  const status = String(row?.status ?? (closeTimestampMs != null ? 'closed' : 'open')).trim().toLowerCase() === 'open' ? 'open' : 'closed';
  const isOpen = status === 'open';
  const idSeed = stableDedupeKey || toTrimmedString(row?.id) || `${symbol}|${openTimestampMs ?? 0}`;
  const storageId = `persisted-${toTrimmedString(row?.id) || stableDedupeKey || hashStringToPositiveInt(idSeed)}`;
  const tradeId = stableDedupeKey || toTrimmedString(row?.id) || storageId;

  const normalizedExecutions = executions.map((execution: PersistedCsvTradeExecution) => ({
    ...execution,
    tradeId,
    persistedTradeId: toTrimmedString(row?.id) || undefined,
    sourceType,
    sourceBroker,
    sourceAccountRef: sourceAccountRef || undefined,
    assetClass: execution.assetClass ?? assetClass,
    canonicalAssetClass: execution.canonicalAssetClass ?? canonicalAssetClass,
    side: String(execution.side ?? '').trim().toLowerCase() || 'buy',
    qty: Number.isFinite(Number(execution.qty ?? execution.quantity)) ? Number(execution.qty ?? execution.quantity) : 0,
    quantity: Number.isFinite(Number(execution.qty ?? execution.quantity)) ? Number(execution.qty ?? execution.quantity) : 0,
    timestamp: normalizeTimestampToMs(execution.timestamp ?? execution.time ?? execution.executedAt) ?? openTimestampMs ?? undefined,
    openTimestampMs: normalizeTimestampToMs(execution.timestamp ?? execution.time ?? execution.executedAt) ?? openTimestampMs ?? undefined,
    orderId: toTrimmedString(execution.orderId ?? execution.externalOrderId) || undefined,
  }));

  return normalizeTradeAssetClassification({
    id: hashStringToPositiveInt(idSeed),
    tradeId,
    persistedTradeId: toTrimmedString(row?.id) || undefined,
    storageId,
    source: sourceType === 'manual' ? 'manual' : 'csv',
    isCustom: sourceType === 'manual',
    persistedSource: 'persisted',
    sourceType,
    sourceBroker,
    sourceAccountRef: sourceAccountRef || undefined,
    sourceAccountId: toTrimmedString(row?.source_account_id ?? row?.sourceAccountId ?? row?.metadata?.accountId) || undefined,
    account: toTrimmedString(row?.metadata?.accountName) || undefined,
    notes: toTrimmedString(row?.metadata?.notes) || undefined,
    tags: Array.isArray(row?.metadata?.tags) ? row.metadata.tags.map((tag: unknown) => toTrimmedString(tag)).filter(Boolean) : [],
    initialRisk: toFiniteNumber(row?.metadata?.initialRiskAmount ?? row?.metadata?.initialRisk),
    initialRiskAmount: toFiniteNumber(row?.metadata?.initialRiskAmount ?? row?.metadata?.initialRisk),
    initialRiskUnset: row?.metadata?.initialRiskUnset === true,
    initialRiskMethod: toTrimmedString(row?.metadata?.initialRiskMethod) || undefined,
    initialStopPrice: toFiniteNumber(row?.metadata?.initialStopPrice),
    manualEntry: sourceType === 'manual' ? {
      date: toTrimmedString(row?.metadata?.entryDate) || undefined,
      time: row?.metadata?.entryTime == null ? null : toTrimmedString(row.metadata.entryTime),
      exitDate: toTrimmedString(row?.metadata?.exitDate) || undefined,
      exitTime: row?.metadata?.exitTime == null ? null : toTrimmedString(row.metadata.exitTime),
      timeZone: toTrimmedString(row?.metadata?.timeZone) || undefined,
      assetType: toTrimmedString(row?.metadata?.assetType) || undefined,
      commissions: toFiniteNumber(row?.metadata?.commissions),
      otherFees: toFiniteNumber(row?.metadata?.otherFees),
      grossPnlSource: toTrimmedString(row?.metadata?.grossPnlSource) || undefined,
      netPnlSource: toTrimmedString(row?.metadata?.netPnlSource) || undefined,
      contractMultiplier: toFiniteNumber(row?.metadata?.contractMultiplier),
      option: row?.metadata?.option || undefined,
    } : undefined,
    importBatchId: toTrimmedString(row?.import_batch_id ?? row?.importBatchId) || undefined,
    stableDedupeKey: stableDedupeKey || undefined,
    stable_dedupe_key: stableDedupeKey || undefined,
    symbol,
    side,
    openDate: openTimestampMs != null ? new Date(openTimestampMs).toISOString().slice(0, 10) : undefined,
    closeDate: closeTimestampMs != null ? new Date(closeTimestampMs).toISOString().slice(0, 10) : null,
    openTimestampMs: openTimestampMs ?? undefined,
    closeTimestampMs: closeTimestampMs ?? null,
    timestamp: openTimestampMs ?? undefined,
    openTime: openTimestampMs ?? undefined,
    closeTime: closeTimestampMs ?? null,
    date: openTimestampMs != null ? new Date(openTimestampMs).toISOString().slice(0, 10) : undefined,
    duration: !isOpen && openTimestampMs != null && closeTimestampMs != null && closeTimestampMs >= openTimestampMs
      ? Math.max(0, Math.round((closeTimestampMs - openTimestampMs) / 60000))
      : undefined,
    entryPrice: entryPrice ?? undefined,
    exitPrice: isOpen ? undefined : exitPrice ?? undefined,
    volume: executedQuantity,
    quantity: executedQuantity,
    executions: normalizedExecutions,
    executionsList: normalizedExecutions,
    sourceOrderIds: Array.isArray(row?.external_order_ids)
      ? row.external_order_ids.map((value: unknown) => toTrimmedString(value)).filter(Boolean)
      : [],
    executionCount,
    pnl: isOpen ? null : netPnl ?? null,
    calculatedPnl: isOpen ? null : netPnl ?? null,
    brokerReportedPnl: toFiniteNumber(row?.metadata?.brokerReportedPnl) ?? null,
    pnlDifference: null,
    grossPnl: isOpen ? undefined : grossPnl ?? undefined,
    gross_pnl: isOpen ? undefined : grossPnl ?? undefined,
    netPnl: isOpen ? undefined : netPnl ?? undefined,
    net_pnl: isOpen ? undefined : netPnl ?? undefined,
    commission: fees,
    fees,
    status,
    assetClass,
    canonicalAssetClass,
    secType: rawInstrumentType,
    rawInstrumentType,
    ...(toTrimmedString(row?.metadata?.instrumentType) ? { instrumentType: toTrimmedString(row?.metadata?.instrumentType) } : {}),
    ...(toTrimmedString(row?.metadata?.canonicalSymbol) ? { canonicalSymbol: toTrimmedString(row?.metadata?.canonicalSymbol) } : {}),
    ...(toTrimmedString(row?.metadata?.indexName) ? { indexName: toTrimmedString(row?.metadata?.indexName) } : {}),
  });
}

export function normalizePersistedCsvTradesResponse(response: unknown): PersistedCsvTradeRecord[] {
  const trades = Array.isArray(response)
    ? response
    : Array.isArray((response as PersistedCsvTradesResponse | null)?.trades)
      ? ((response as PersistedCsvTradesResponse).trades as any[])
      : [];

  return trades
    .filter((trade) => trade && typeof trade === 'object')
    .map((trade) => normalizePersistedCsvTradeRecord(trade))
    .filter(Boolean);
}

export type PersistedCsvTradesPage = {
  trades: PersistedCsvTradeRecord[];
  hasMore: boolean;
  nextCursor: string | null;
  nextOffset: number | null;
  total: number | null;
};

type PersistedCsvTradesPageOptions = {
  limit?: number;
  offset?: number;
  cursor?: string | null;
  importBatchId?: string;
  signal?: AbortSignal;
};

/**
 * Reads ONE server page. Supports the keyset `cursor` returned by the server
 * and falls back to legacy offset paging when the server does not return
 * pagination metadata (older deployments).
 */
export async function fetchPersistedCsvTradesPage(options: PersistedCsvTradesPageOptions = {}): Promise<PersistedCsvTradesPage> {
  const scope = getVerifiedPrivateTradingScope();
  const scopeGeneration = getPrivateTradingScopeGeneration();
  if (!scope) throw new Error('private_trade_scope_unverified');
  const session = await getCurrentSession();
  if (!isPrivateTradingScopeCurrent(scope, scopeGeneration) || String(session?.user?.id ?? '').trim() !== scope.userId) {
    throw new Error('private_trade_scope_changed');
  }
  const accessToken = toTrimmedString(session?.access_token);

  if (!accessToken) {
    throw new Error('missing_access_token');
  }

  const limit = toPositiveInt(options.limit, DEFAULT_LIMIT);
  const cursor = toTrimmedString(options.cursor);
  const offset = cursor ? 0 : Math.max(0, Number.parseInt(String(options.offset ?? 0), 10) || 0);
  const batchId = toTrimmedString(options.importBatchId);
  const batchQuery = batchId ? `&importBatchId=${encodeURIComponent(batchId)}` : '';
  const cursorQuery = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
  const url = buildUrl(`/api/trades/persisted?limit=${limit}&offset=${offset}${cursorQuery}${batchQuery}`);
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };

  applyTenantRequestHeaders(headers);

  const init: RequestInit = { method: 'GET', headers };
  if (options.signal) init.signal = options.signal;
  const response = await fetch(url, init);
  if (!isPrivateTradingScopeCurrent(scope, scopeGeneration)) throw new Error('private_trade_scope_changed');

  const text = await response.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }

  if (!response.ok) {
    const message = data?.error || data?.message || `HTTP ${response.status}`;
    throw new Error(message);
  }

  const rawRows = Array.isArray(data) ? data : (Array.isArray(data?.trades) ? data.trades : []);
  const trades = normalizePersistedCsvTradesResponse(data);
  const serverReportsPaging = data && typeof data === 'object' && !Array.isArray(data) && typeof data.hasMore === 'boolean';
  const nextCursor = serverReportsPaging ? (toTrimmedString(data.nextCursor) || null) : null;
  if (serverReportsPaging && data.hasMore && !nextCursor) {
    throw new Error('persisted_trade_pagination_stalled');
  }
  const hasMore = serverReportsPaging ? Boolean(data.hasMore) : rawRows.length >= limit;
  const totalValue = toFiniteNumber(data?.total);
  return {
    trades,
    hasMore,
    nextCursor,
    nextOffset: !serverReportsPaging && hasMore ? offset + rawRows.length : null,
    total: totalValue != null && totalValue >= 0 ? totalValue : null,
  };
}

export async function fetchPersistedCsvTrades(options: { limit?: number; offset?: number; importBatchId?: string } = {}): Promise<PersistedCsvTradeRecord[]> {
  const page = await fetchPersistedCsvTradesPage(options);
  return page.trades;
}

// ---------------------------------------------------------------------------
// Complete-history status (consumed by the Trades page footer).
// ---------------------------------------------------------------------------

export type PersistedTradeHistoryStatus = {
  phase: 'idle' | 'loading' | 'complete' | 'error';
  loaded: number;
  total: number | null;
  error: string | null;
};

const IDLE_HISTORY_STATUS: PersistedTradeHistoryStatus = { phase: 'idle', loaded: 0, total: null, error: null };
let persistedHistoryStatus: PersistedTradeHistoryStatus = IDLE_HISTORY_STATUS;
const persistedHistoryStatusListeners = new Set<() => void>();

function setPersistedHistoryStatus(next: PersistedTradeHistoryStatus) {
  const prev = persistedHistoryStatus;
  if (prev.phase === next.phase && prev.loaded === next.loaded && prev.total === next.total && prev.error === next.error) return;
  persistedHistoryStatus = next;
  persistedHistoryStatusListeners.forEach((listener) => {
    try { listener(); } catch { /* listener errors must not break loading */ }
  });
}

export function getPersistedTradeHistoryStatus(): PersistedTradeHistoryStatus {
  return persistedHistoryStatus;
}

export function subscribePersistedTradeHistoryStatus(listener: () => void): () => void {
  persistedHistoryStatusListeners.add(listener);
  return () => { persistedHistoryStatusListeners.delete(listener); };
}

export const PERSISTED_TRADE_HISTORY_RETRY_EVENT = 'trade-proof-persisted-history-retry';

/** Asks the authoritative trade hook to resume a partially failed history read. */
export function requestPersistedTradeHistoryRetry() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PERSISTED_TRADE_HISTORY_RETRY_EVENT));
}

// Interim progress is published after the first page and then every N pages,
// so the Trades page paints quickly without re-merging on every batch.
const PROGRESS_PUBLISH_EVERY_PAGES = 10;
const LATER_PAGE_RETRY_DELAYS_MS = [400, 1500, 4000];

type PersistedProgressListener = (trades: PersistedCsvTradeRecord[]) => void;
let persistedProgressListeners = new Set<PersistedProgressListener>();
let persistedCurrentProgress: PersistedCsvTradeRecord[] | null = null;
let persistedTradesAbortController: AbortController | null = null;
// Resume point kept after a later page fails, so a retry continues from the
// failed cursor instead of re-reading (and never duplicating) loaded rows.
let persistedResumeState: { scopeKey: string; trades: PersistedCsvTradeRecord[]; cursor: string | null; offset: number | null; total: number | null } | null = null;

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new Error('private_trade_scope_changed')); return; }
    const timer = setTimeout(() => { signal.removeEventListener('abort', onAbort); resolve(); }, ms);
    const onAbort = () => { clearTimeout(timer); reject(new Error('private_trade_scope_changed')); };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function persistedRowIdentity(trade: PersistedCsvTradeRecord): string {
  return toTrimmedString(trade?.persistedTradeId) || toTrimmedString(trade?.stableDedupeKey) || toTrimmedString(trade?.storageId);
}

async function readCompletePersistedHistory(args: {
  scopeKey: string;
  isCurrent: () => boolean;
  signal: AbortSignal;
  resume: boolean;
  onProgress: (trades: PersistedCsvTradeRecord[]) => void;
}): Promise<PersistedCsvTradeRecord[]> {
  const resumeFrom = args.resume && persistedResumeState?.scopeKey === args.scopeKey ? persistedResumeState : null;
  persistedResumeState = null;
  const collected: PersistedCsvTradeRecord[] = resumeFrom ? [...resumeFrom.trades] : [];
  const seen = new Set<string>(collected.map(persistedRowIdentity).filter(Boolean));
  let cursor: string | null = resumeFrom?.cursor ?? null;
  let offset: number | null = resumeFrom?.offset ?? null;
  let total: number | null = resumeFrom?.total ?? null;
  let isFirstPage = !resumeFrom;
  let pagesSincePublish = 0;
  const seenCursors = new Set<string>();

  setPersistedHistoryStatus({ phase: 'loading', loaded: collected.length, total, error: null });

  for (;;) {
    let page: PersistedCsvTradesPage | null = null;
    let attempt = 0;
    for (;;) {
      try {
        page = await fetchPersistedCsvTradesPage({ limit: DEFAULT_LIMIT, cursor, offset: offset ?? 0, signal: args.signal });
        break;
      } catch (error) {
        if (!args.isCurrent() || args.signal.aborted) throw new Error('private_trade_scope_changed');
        // The first page keeps the existing contract: failures reject so the
        // caller never treats an unverified read as authoritative.
        if (isFirstPage) {
          setPersistedHistoryStatus({ phase: 'error', loaded: 0, total: null, error: String((error as Error)?.message || error) });
          throw error;
        }
        if (attempt >= LATER_PAGE_RETRY_DELAYS_MS.length) {
          persistedResumeState = { scopeKey: args.scopeKey, trades: [...collected], cursor, offset, total };
          setPersistedHistoryStatus({ phase: 'error', loaded: collected.length, total, error: String((error as Error)?.message || error) });
          // Never publish a truncated history as an authoritative successful read.
          throw error;
        }
        await wait(LATER_PAGE_RETRY_DELAYS_MS[attempt], args.signal);
        attempt += 1;
      }
    }
    if (!args.isCurrent()) throw new Error('private_trade_scope_changed');

    if (page.total != null) total = page.total;
    for (const trade of page.trades) {
      const identity = persistedRowIdentity(trade);
      if (identity) {
        if (seen.has(identity)) continue;
        seen.add(identity);
      }
      collected.push(trade);
    }

    const done = !page.hasMore;
    if (!done) {
      if (page.nextCursor) {
        if (seenCursors.has(page.nextCursor)) throw new Error('persisted_trade_pagination_stalled');
        seenCursors.add(page.nextCursor);
        cursor = page.nextCursor;
        offset = null;
      } else if (page.nextOffset != null && page.nextOffset > (offset ?? 0)) {
        offset = page.nextOffset;
        cursor = null;
      } else {
        throw new Error('persisted_trade_pagination_stalled');
      }
    }

    pagesSincePublish += 1;
    if (done) break;
    setPersistedHistoryStatus({ phase: 'loading', loaded: collected.length, total, error: null });
    if (isFirstPage || pagesSincePublish >= PROGRESS_PUBLISH_EVERY_PAGES) {
      pagesSincePublish = 0;
      args.onProgress([...collected]);
    }
    isFirstPage = false;
  }

  if (total != null && collected.length !== total) {
    const error = new Error(`persisted_trade_history_incomplete: expected ${total} trades, received ${collected.length}`);
    setPersistedHistoryStatus({ phase: 'error', loaded: collected.length, total, error: error.message });
    throw error;
  }
  setPersistedHistoryStatus({ phase: 'complete', loaded: collected.length, total: total ?? collected.length, error: null });
  return collected;
}

/**
 * Loads the authenticated user's persisted trades.
 *
 * Without explicit paging options this reads the COMPLETE persisted history in
 * sequential 500-row keyset batches, so every consumer (Trades, Dashboard,
 * Trend, Calendar, Journal, widgets) computes over the full dataset rather than
 * the newest 500. `onProgress` receives interim snapshots while later batches
 * are still loading. Passing `limit`/`offset`/`importBatchId` keeps the legacy
 * single-page behavior.
 */
export async function loadPersistedCsvTrades(options: {
  limit?: number;
  offset?: number;
  forceRefresh?: boolean;
  importBatchId?: string;
  resume?: boolean;
  onProgress?: PersistedProgressListener;
} = {}): Promise<PersistedCsvTradeRecord[]> {
  const scope = getVerifiedPrivateTradingScope();
  const generation = getPrivateTradingScopeGeneration();
  if (!scope) throw new Error('private_trade_scope_unverified');
  const scopeKey = `${scope.tenantId}:${scope.userId}:${generation}`;
  if (persistedTradesCacheScope !== scopeKey) {
    persistedTradesCache = null;
    persistedTradesCacheReady = false;
    persistedTradesCachePromise = null;
    persistedResumeState = null;
    persistedCurrentProgress = null;
    persistedTradesCacheScope = scopeKey;
  }
  if (!options.forceRefresh && persistedTradesCacheReady && persistedTradesCache) {
    return persistedTradesCache;
  }

  // Forced refreshes bypass the resolved cache, but share an authoritative
  // read already in progress so neither response invalidates the other.
  if (persistedTradesCachePromise) {
    if (options.onProgress) {
      persistedProgressListeners.add(options.onProgress);
      if (persistedCurrentProgress) options.onProgress(persistedCurrentProgress);
    }
    return persistedTradesCachePromise;
  }

  const requestGeneration = ++persistedTradesRequestGeneration;
  const isCurrent = () => isPrivateTradingScopeCurrent(scope, generation) && requestGeneration === persistedTradesRequestGeneration;
  const singlePage = options.limit != null || options.offset != null || Boolean(toTrimmedString(options.importBatchId));
  const progressListeners = new Set<PersistedProgressListener>();
  if (options.onProgress) progressListeners.add(options.onProgress);
  persistedProgressListeners = progressListeners;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  persistedTradesAbortController = controller;

  const read = singlePage
    ? fetchPersistedCsvTrades({ limit: options.limit, offset: options.offset, importBatchId: options.importBatchId })
    : readCompletePersistedHistory({
      scopeKey,
      isCurrent,
      signal: controller?.signal ?? new AbortController().signal,
      resume: Boolean(options.resume),
      onProgress: (trades) => {
        if (!isCurrent()) return;
        persistedCurrentProgress = trades;
        progressListeners.forEach((listener) => {
          try { listener(trades); } catch { /* ignore listener failures */ }
        });
      },
    });

  const pending = read
    .then((trades) => {
      if (!isCurrent()) throw new Error('private_trade_scope_changed');
      persistedTradesCache = trades;
      persistedTradesCacheScope = scopeKey;
      persistedTradesCacheReady = true;
      persistedCurrentProgress = null;
      return trades;
    })
    .catch((error) => {
      if (requestGeneration === persistedTradesRequestGeneration) {
        persistedTradesCache = null;
        persistedTradesCacheReady = false;
        persistedCurrentProgress = null;
        if (!singlePage && persistedHistoryStatus.phase === 'loading') {
          setPersistedHistoryStatus({
            ...persistedHistoryStatus,
            phase: 'error',
            error: String((error as Error)?.message || error),
          });
        }
      }
      throw error;
    })
    .finally(() => {
      if (requestGeneration === persistedTradesRequestGeneration) {
        persistedTradesCachePromise = null;
        if (persistedTradesAbortController === controller) persistedTradesAbortController = null;
      }
    });

  persistedTradesCachePromise = pending;
  return pending;
}

export function resetPersistedCsvTradesCache() {
  persistedTradesRequestGeneration += 1;
  persistedTradesAbortController?.abort();
  persistedTradesAbortController = null;
  persistedProgressListeners = new Set();
  persistedCurrentProgress = null;
  persistedResumeState = null;
  persistedTradesCache = null;
  persistedTradesCacheReady = false;
  persistedTradesCachePromise = null;
  persistedTradesCacheScope = '';
  setPersistedHistoryStatus(IDLE_HISTORY_STATUS);
}

export function mergePersistedCsvTradesWithLocalTrades(
  localTrades: any[],
  persistedTrades: any[],
  options: { persistedReadVerified?: boolean; persistedReadPartial?: boolean } = {}
): any[] {
  const merged: any[] = [];
  const localIndexesByIdentity = new Map<string, number>();
  const localIndexesByManualIdentity = new Map<string, number>();
  const mergedServerIdentities = new Set<string>();
  const mergedServerManualIdentities = new Set<string>();
  const persistedIdentities = new Set<string>();
  const persistedManualIdentities = new Set<string>();
  const localRows = Array.isArray(localTrades) ? localTrades : [];
  const persistedRows = Array.isArray(persistedTrades) ? persistedTrades : [];

  for (const trade of localRows) {
    const sourceType = normalizeSourceType(trade?.sourceType ?? trade?.source ?? trade?.source_type);
    const manualIdentityKey = derivePersistedManualIdentityKey(trade);
    const isServerBackedManual = sourceType === 'manual'
      && toTrimmedString(trade?.persistedSource).toLowerCase() === 'persisted';
    if (options.persistedReadVerified === false && (sourceType === 'csv' || sourceType === 'broker_sync')) continue;
    if (sourceType === 'manual' && (manualIdentityKey || isServerBackedManual)
      && (options.persistedReadVerified === false || (options.persistedReadVerified === true && !manualIdentityKey))) continue;

    const index = merged.length;
    merged.push(trade);

    const identityKey = deriveTradeIdentityKey(trade);
    if (identityKey && !localIndexesByIdentity.has(identityKey)) localIndexesByIdentity.set(identityKey, index);
    if (manualIdentityKey && !localIndexesByManualIdentity.has(manualIdentityKey)) localIndexesByManualIdentity.set(manualIdentityKey, index);
  }

  for (const trade of persistedRows) {
    const manualIdentityKey = derivePersistedManualIdentityKey(trade);
    if (manualIdentityKey) {
      persistedManualIdentities.add(manualIdentityKey);
      if (localIndexesByManualIdentity.has(manualIdentityKey)) {
        if (!mergedServerManualIdentities.has(manualIdentityKey)) {
          const index = localIndexesByManualIdentity.get(manualIdentityKey)!;
          const localTrade = merged[index];
          merged[index] = {
            ...localTrade,
            ...trade,
            notes: localTrade?.notes ?? trade?.notes,
            tags: Array.isArray(localTrade?.tags) ? localTrade.tags : (Array.isArray(trade?.tags) ? trade.tags : []),
          };
          mergedServerManualIdentities.add(manualIdentityKey);
        }
        continue;
      }

      if (persistedManualIdentities.has(manualIdentityKey) && mergedServerManualIdentities.has(manualIdentityKey)) continue;
      merged.push(trade);
      mergedServerManualIdentities.add(manualIdentityKey);
      continue;
    }

    const identityKey = deriveTradeIdentityKey(trade);
    if (identityKey && localIndexesByIdentity.has(identityKey)) {
      if (!mergedServerIdentities.has(identityKey)) {
        const index = localIndexesByIdentity.get(identityKey)!;
        const localTrade = merged[index];
        merged[index] = {
          ...localTrade,
          ...trade,
          // Notes and tags are user-authored metadata; all financial and execution
          // fields intentionally come from the authenticated persisted record.
          notes: localTrade?.notes ?? trade?.notes,
          tags: Array.isArray(localTrade?.tags) ? localTrade.tags : (Array.isArray(trade?.tags) ? trade.tags : []),
        };
        mergedServerIdentities.add(identityKey);
      }
      continue;
    }

    if (identityKey && persistedIdentities.has(identityKey)) continue;
    merged.push(trade);
    if (identityKey) persistedIdentities.add(identityKey);
  }

  // Pruning local manual rows that are absent server-side is only safe once the
  // COMPLETE persisted history has been read; a partial (still paging or failed
  // mid-history) snapshot simply may not contain them yet.
  if (options.persistedReadVerified === true && options.persistedReadPartial !== true) {
    for (let index = merged.length - 1; index >= 0; index -= 1) {
      const localManualIdentity = derivePersistedManualIdentityKey(merged[index]);
      if (localManualIdentity && !persistedManualIdentities.has(localManualIdentity)) merged.splice(index, 1);
    }
  }

  // Gapless visible-ID normalization over the COMPLETE final collection AFTER all
  // sources (local imported store + server persisted-CSV store) have been merged
  // and deduplicated. The table renders each record's persisted `tradeId` directly,
  // so the merged collection must carry the account-level T1..Tn sequence — never
  // the server's per-row counter (e.g. T7, T9, ...) or any other source's ids.
  // Internal IDs and source trade IDs are untouched; re-running reports zero changes.
  const normalized = normalizeVisibleTradeIds(merged);
  return normalized.records;
}
